import { Box, FormLabel, Paper, Stack, Typography } from '@mui/material';
import type { PropsWithChildren, ReactNode } from 'react';
import { mergeSx } from '../../../../utils/styles';

const styles = {
  columns: {
    display: 'grid',
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 360px) minmax(0, 1fr)' },
    gap: 2,
    alignItems: 'start',
  },
  reviewColumns: {
    alignItems: 'stretch',
    '& > *': {
      height: '100%',
      '& > :last-child': {
        flexGrow: 1,
      },
    },
  },
  column: {
    minWidth: 0,
  },
  section: {
    p: 2,
    borderRadius: 2,
    bgcolor: 'action.hover',
  },
  sectionTitle: {
    typography: 'h6',
  },
  reviewGrid: {
    display: 'grid',
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
    gap: 2,
    m: 0,
  },
  reviewValue: {
    m: 0,
    mt: 0.25,
    overflowWrap: 'anywhere',
  },
} as const;

export const WelcomeBonusFormColumns = ({ children }: PropsWithChildren) => <Box sx={styles.columns}>{children}</Box>;

export const WelcomeBonusReviewColumns = ({ children }: PropsWithChildren) => (
  <Box sx={mergeSx(styles.columns, styles.reviewColumns)}>{children}</Box>
);

export const WelcomeBonusFormColumn = ({ children }: PropsWithChildren) => (
  <Stack spacing={2} sx={styles.column}>
    {children}
  </Stack>
);

type WelcomeBonusFormSectionProps = PropsWithChildren<{
  title: string;
}>;

export const WelcomeBonusFormSection = ({ title, children }: WelcomeBonusFormSectionProps) => (
  <Paper variant='outlined' sx={styles.section}>
    <Stack spacing={2}>
      <FormLabel sx={styles.sectionTitle}>{title}</FormLabel>
      {children}
    </Stack>
  </Paper>
);

export const WelcomeBonusReviewGrid = ({ children }: PropsWithChildren) => (
  <Box component='dl' sx={styles.reviewGrid}>
    {children}
  </Box>
);

type WelcomeBonusReviewFieldProps = {
  label: string;
  value?: ReactNode | null;
};

export const WelcomeBonusReviewField = ({ label, value }: WelcomeBonusReviewFieldProps) => (
  <Box>
    <Typography
      component='dt'
      variant='caption'
      sx={{
        color: 'text.secondary',
      }}
    >
      {label}
    </Typography>
    <Typography
      component='dd'
      variant='body2'
      sx={mergeSx(
        {
          fontWeight: 500,
        },
        styles.reviewValue
      )}
    >
      {value ?? 'Unavailable'}
    </Typography>
  </Box>
);
